import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useTestKit, useUpdateTestKit } from "@/hooks/use-test-kits";
import { TestKitForm, type KitFormFieldError } from "@/components/test-kits/test-kit-form";
import { PageHeader, Breadcrumbs, PageSkeleton } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/test-kits/$id/edit")({
  component: TestKitEditPage,
});

function TestKitEditPage() {
  const { t } = useTranslation("test-kits");
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { data: kit, isLoading } = useTestKit(id);
  const updateMutation = useUpdateTestKit(id);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [fieldError, setFieldError] = useState<KitFormFieldError | undefined>(undefined);

  if (isLoading || !kit) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <Breadcrumbs
        items={[
          { label: t("list.title"), to: "/testers", search: { tab: "kits" } },
          { label: kit.serial_number, to: `/test-kits/${id}` },
          { label: t("form.editTitle") },
        ]}
      />
      <PageHeader title={t("form.editTitle")} subtitle={kit.serial_number} />

      <div className="mt-6">
        <TestKitForm
          initial={kit}
          isSubmitting={updateMutation.isPending}
          submitError={submitError}
          fieldError={fieldError}
          onCancel={() => void navigate({ to: "/test-kits/$id", params: { id } })}
          onSubmit={(body) => {
            setSubmitError(undefined);
            setFieldError(undefined);
            updateMutation.mutate(body, {
              onSuccess: () => {
                void navigate({ to: "/test-kits/$id", params: { id } });
              },
              onError: (err) => {
                if (err instanceof ApiError) {
                  if (err.code === "kit_serial_conflict_for_owner") {
                    setFieldError("serialConflict");
                    return;
                  }
                  if (err.code === "kit_record_immutable_post_soft_delete") {
                    setSubmitError(t("errors.softDeleted"));
                    return;
                  }
                  setSubmitError(err.message);
                  return;
                }
                setSubmitError(err instanceof Error ? err.message : t("errors.updateFailed"));
              },
            });
          }}
        />
      </div>
    </div>
  );
}
